import { useMemo, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useCloseOnRouteChange } from '../../../Hooks/useCloseOnRouteChange';
import { useDismissOnOutside } from '../../../Hooks/useDismissOnOutside';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import { buildMenuGroups } from '../../../utils/helpers/buildMenuGroups';
import { toTestId } from '../../../utils/helpers/toTestId';
import { buildMenuTabs, isTabActive } from './buildMenuTabs';
import MenuColumn from './MenuColumn';
import { DASHBOARD_CATEGORY, SETTINGS_CATEGORY } from './menuTabConfig';
import { useStoredMenu } from './useStoredMenu';
import './NavMenu.css';

const CLOSED_MENU = { category: null, pinned: false };

// Shared by NavMenu and MobileMenu so both surfaces render the exact same
// tabs/columns/order — see buildMenuTabs.js for the menuOrder convention.
export const useNavTabs = () => {
  const menu = useStoredMenu();
  return useMemo(
    () => buildMenuTabs(menu).filter((tab) => tab.category !== SETTINGS_CATEGORY && tab.category !== DASHBOARD_CATEGORY),
    [menu]
  );
};

// Settings stays its own backend group (not merged with Manage Accounts,
// which keeps living as its own tab in NavMenu) — shared by AccountMenu and MobileMenu.
export const useSettingsItems = () => {
  const menu = useStoredMenu();
  return useMemo(() => {
    const groups = buildMenuGroups(menu);
    return groups.find((group) => group.category === SETTINGS_CATEGORY)?.children || [];
  }, [menu]);
};

// Menu state is { category, pinned }: hover opens a panel, clicking the trigger pins it
// so it survives the pointer leaving. Without the pin, clicking a tab that hover already
// opened would read as "toggle off" and close the panel the user was aiming at.
const NavMenu = () => {
  const { pathname } = useLocation();
  const { tempedgeTranslator, locale: lang } = useTempEdgeTranslator();
  const containerRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(CLOSED_MENU);
  const tabs = useNavTabs();

  useCloseOnRouteChange(() => setOpenMenu(CLOSED_MENU));
  useDismissOnOutside(containerRef, Boolean(openMenu.category), () => setOpenMenu(CLOSED_MENU));

  const handleMouseEnter = (category) => setOpenMenu((prev) => (prev.category === category ? prev : { category, pinned: false }));

  const handleMouseLeave = (category) => setOpenMenu((prev) => (prev.category === category && !prev.pinned ? CLOSED_MENU : prev));

  const handleTriggerClick = (category) =>
    setOpenMenu((prev) => (prev.category === category && prev.pinned ? CLOSED_MENU : { category, pinned: true }));

  return (
    <nav className="te-nav-menu" ref={containerRef} aria-label={tempedgeTranslator('com.tempedge.msg.nav.open_menu')}>
      <ul className="te-nav-menu-list">
        {tabs.map((tab) => {
          const testId = toTestId(tab.category);

          if (tab.columns.length === 0) {
            const targetPath = `${tab.optionPath}/${lang}`;
            const isActive = pathname === targetPath;
            return (
              <li key={tab.category} className={`te-nav-menu-item ${isActive ? 'active' : ''}`}>
                <Link className="te-nav-menu-trigger te-focus-ring" to={targetPath} data-testid={`navmenu-${testId}`}>
                  {tempedgeTranslator(tab.category)}
                </Link>
              </li>
            );
          }

          const isOpen = openMenu.category === tab.category;
          const hasActiveChild = isTabActive(tab, pathname, lang);

          return (
            <li
              key={tab.category}
              className={`te-nav-menu-item ${isOpen || hasActiveChild ? 'active' : ''}`}
              onMouseEnter={() => handleMouseEnter(tab.category)}
              onMouseLeave={() => handleMouseLeave(tab.category)}
            >
              <button
                type="button"
                className="te-nav-menu-trigger te-focus-ring"
                aria-expanded={isOpen}
                onClick={() => handleTriggerClick(tab.category)}
                data-testid={`navmenu-${testId}`}
              >
                {tempedgeTranslator(tab.category)}
                <FaChevronDown className={`te-nav-menu-chevron ${isOpen ? 'open' : ''}`} />
              </button>

              <div className={`te-nav-menu-panel ${isOpen ? 'show' : ''}`} data-testid={`navmenu-panel-${testId}`}>
                {tab.columns.map((column) => (
                  <MenuColumn
                    key={column.key}
                    column={column}
                    lang={lang}
                    colClassName="te-nav-menu-col"
                    labelClassName="te-nav-menu-section-label"
                    iconClassName="te-menu-item-icon te-nav-menu-item-icon"
                    linkClassName="te-focus-ring"
                    onItemClick={() => setOpenMenu(CLOSED_MENU)}
                  />
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default NavMenu;
